// apps/content/content/data/sectors.ts

import type { SectorContext } from "@/lib/content-types";

/**
 * Sector definitions for city hubs.
 *
 * Used by:
 *   - Bergamo tier-1 hub (5 sectors)
 *   - Treviglio, Caravaggio, Romano di Lombardia tier-2 hubs (2 sectors each)
 *
 * Each sector provides unique editorial substance per city:
 * shortDescription is the UNIQUE ELEMENT anti-doorway.
 */

// ─── Bergamo sectors (5) ────────────────────────────────────────────────────

export const SECTOR_MECCANICA: SectorContext = {
  slug: "meccanica",
  name: "Meccanica e automazione industriale",
  shortDescription:
    "Distretto bergamasco meccanico copre dalla precisione CNC all'automazione robotica. Pattern ricorrenti: fiere settore (Mecspe, EMO Milano), esigenza configuratore prodotto, multilingua tecnico (EN/DE), area B2B con listini riservati. Spesso clienti longevi che valutano fornitori per anni: il sito serve come prima qualifica tecnica.",
  typicalClient: "PMI 10-80 dipendenti, fatturato 2-20M, export EU",
  commonNeeds: [
    "multilingua tecnico EN/DE",
    "configuratore prodotto",
    "area B2B con listini riservati",
    "schede tecniche scaricabili",
    "case study video",
  ],
  recommendedServices: [
    "siti-wordpress",
    "sviluppo-custom-nextjs",
    "automazioni-n8n-ai",
  ],
};

export const SECTOR_EDILIZIA: SectorContext = {
  slug: "edilizia",
  name: "Edilizia e serramenti",
  shortDescription:
    "Edilizia e serramenti bergamasca spazia da posa infissi a impianti industriali. Pattern tipici: gallery lavori con prima/dopo, preventivatore online che filtra i lead, certificazioni in evidenza (CasaClima, CE), integrazione con CRM gestionale cantieri. La vicinanza a Milano porta richieste ville e ristrutturazioni premium.",
  typicalClient: "PMI 5-40 dipendenti, 0.5-10M fatturato, area Bergamo + Milano",
  commonNeeds: [
    "gallery lavori con filtri per tipologia",
    "preventivatore online intelligente",
    "certificazioni in evidenza",
    "CRM gestionale cantieri integrato",
    "recensioni Google automatizzate",
  ],
  recommendedServices: [
    "siti-wordpress",
    "automazioni-n8n-ai",
    "wordpress-performance",
  ],
};

export const SECTOR_AGRICOLTURA: SectorContext = {
  slug: "agricoltura",
  name: "Agricoltura e aziende agricole",
  shortDescription:
    "Aziende agricole bergamasche vanno dal biologico certificato alla grande distribuzione filiera corta. Pattern: storytelling territoriale forte, vendita diretta online (e-commerce stagionale), agriturismo collegato. Target misto B2B (rivenditori, GDO) + B2C (privati che comprano direttamente). Esigenza di mostrare tracciabilità e certificazioni in modo trasparente.",
  typicalClient: "Azienda agricola familiare 5-25 dipendenti, vendita diretta + ingrosso",
  commonNeeds: [
    "e-commerce stagionale con prenotazioni",
    "storytelling filiera con foto produzione",
    "certificazioni bio/DOP in evidenza",
    "integrazione con agriturismo e prenotazioni",
    "area rivenditori B2B",
  ],
  recommendedServices: [
    "ecommerce-woocommerce",
    "siti-wordpress",
    "siti-3d-creative",
  ],
};

export const SECTOR_FOOD_BEVERAGE: SectorContext = {
  slug: "food-beverage",
  name: "Food & beverage e ristorazione",
  shortDescription:
    "Food & beverage bergamasco include ristoranti, pasticcerie, produttori formaggi DOP, aziende vinicole. Pattern: menu digitali aggiornabili, prenotazioni online, delivery locale, vendita prodotti tipici online. Stagionalità alta (es. Natale, Pasqua per pasticcerie). Fiere settore (Taste Italy) rilevanti per produttori premium.",
  typicalClient: "Locale ristorazione o produttore artigianale, 3-30 dipendenti",
  commonNeeds: [
    "menu digitali aggiornabili senza dev",
    "prenotazioni online con gestione tavoli",
    "e-commerce stagionale prodotti tipici",
    "integrazione delivery (Glovo, Deliveroo, proprio)",
    "recensioni e storytelling produzione",
  ],
  recommendedServices: [
    "ecommerce-woocommerce",
    "siti-wordpress",
    "automazioni-n8n-ai",
  ],
};

export const SECTOR_SERVIZI_B2B: SectorContext = {
  slug: "servizi-b2b",
  name: "Servizi B2B e studi professionali",
  shortDescription:
    "Studi professionali (commercialisti, avvocati, consulenti) e servizi B2B a Bergamo crescono con riferimento territorio Milano. Pattern: posizionamento competenza verticale (non generalista), lead magnet scaricabili (checklist, report), booking appuntamenti integrato, area riservata clienti con documenti e fatturazione.",
  typicalClient: "Studio professionale 2-15 soci + team, clientela PMI Bergamo + MI",
  commonNeeds: [
    "lead magnet scaricabili (checklist, report)",
    "booking appuntamenti online",
    "area riservata clienti con documenti",
    "blog editoriale competenza verticale",
    "integrazione con fatturazione elettronica",
  ],
  recommendedServices: [
    "siti-wordpress",
    "sviluppo-custom-nextjs",
    "automazioni-n8n-ai",
  ],
};

export const BERGAMO_SECTORS: SectorContext[] = [
  SECTOR_MECCANICA,
  SECTOR_EDILIZIA,
  SECTOR_AGRICOLTURA,
  SECTOR_FOOD_BEVERAGE,
  SECTOR_SERVIZI_B2B,
];

// ─── Treviglio sectors (2) ──────────────────────────────────────────────────

export const TREVIGLIO_SECTORS: SectorContext[] = [
  {
    slug: "logistica-retail",
    name: "Logistica e retail",
    shortDescription:
      "Treviglio è snodo logistico tra Bergamo, Milano e Cremona: hub per e-commerce, distribuzione, retail fisico con componente online. Pattern: integrazione corrieri multipli, preventivatore automatico spedizioni, gestione multi-magazzino, fidelity e-commerce + punti vendita fisici.",
    typicalClient: "Retailer multi-canale o operatore logistico 10-50 dipendenti",
    commonNeeds: [
      "integrazione corrieri multipli (GLS, BRT, SDA, DHL)",
      "preventivatore spedizioni automatico",
      "sync multi-magazzino con gestionale",
      "fidelity card online + negozio fisico",
    ],
    recommendedServices: ["ecommerce-woocommerce", "automazioni-n8n-ai", "siti-wordpress"],
  },
  {
    slug: "metalmeccanica",
    name: "Metalmeccanica trevigliese",
    shortDescription:
      "Distretto metalmeccanico trevigliese produce componentistica per automotive e macchinari industriali. Pattern: schede tecniche complesse (PDF multilingua), 3D model preview, area tecnici clienti con CAD scaricabili, certificazioni ISO in evidenza.",
    typicalClient: "PMI meccanica 15-60 dipendenti, B2B export",
    commonNeeds: [
      "schede tecniche multilingua scaricabili",
      "3D model preview (Spline o GLB)",
      "area clienti tecnici con CAD",
      "certificazioni ISO/9001/14001 in evidenza",
    ],
    recommendedServices: ["siti-wordpress", "sviluppo-custom-nextjs", "siti-3d-creative"],
  },
];

// ─── Caravaggio sectors (2) ─────────────────────────────────────────────────

export const CARAVAGGIO_SECTORS: SectorContext[] = [
  {
    slug: "turismo-ricettivo",
    name: "Turismo e ricettività",
    shortDescription:
      "Caravaggio è meta turistica grazie al Santuario: flusso pellegrini + turismo religioso e culturale. Pattern: prenotazioni online, gestione multi-lingua (IT, EN, DE, ES, PT per pellegrini sudamericani), integrazione con OTA (Booking.com), storytelling territoriale.",
    typicalClient: "B&B, hotel, agriturismo 3-20 camere",
    commonNeeds: [
      "prenotazioni online con channel manager",
      "multilingua 4+ lingue",
      "sync OTA (Booking.com, Expedia, Airbnb)",
      "storytelling territoriale con mappa attrazioni",
    ],
    recommendedServices: ["siti-wordpress", "sviluppo-custom-nextjs", "automazioni-n8n-ai"],
  },
  {
    slug: "agroalimentare",
    name: "Agroalimentare e produzioni tipiche",
    shortDescription:
      "Caravaggio e dintorni producono taleggio DOP, salumi, vini locali: mercato misto tra GDO, ristorazione e vendita diretta turisti. Pattern: e-commerce con spedizione fresco, integrazione con HACCP tracciabilità, storytelling produzione artigianale.",
    typicalClient: "Produttore artigianale 3-15 dipendenti, vendita mista",
    commonNeeds: [
      "e-commerce con spedizioni fresco/freezer",
      "gestione HACCP tracciabilità",
      "storytelling produzione artigianale",
      "integrazione con ristorazione locale (B2B)",
    ],
    recommendedServices: ["ecommerce-woocommerce", "siti-wordpress", "siti-3d-creative"],
  },
];

// ─── Romano di Lombardia sectors (2) ────────────────────────────────────────

export const ROMANO_SECTORS: SectorContext[] = [
  {
    slug: "tessile",
    name: "Tessile e abbigliamento",
    shortDescription:
      "Romano di Lombardia ha distretto tessile storico: filati, confezione, accessori. Pattern: cataloghi stagionali B2B (lookbook digitali), showroom virtuale, integrazione con fiere settore (Milano Unica, Pitti), multilingua tecnico per compratori esteri.",
    typicalClient: "Azienda tessile 20-100 dipendenti, export EU + US",
    commonNeeds: [
      "lookbook digitali stagionali",
      "showroom virtuale 3D",
      "area B2B compratori con listini",
      "multilingua tecnico EN/FR/DE",
    ],
    recommendedServices: ["siti-wordpress", "siti-3d-creative", "ecommerce-woocommerce"],
  },
  {
    slug: "commercio-pmi",
    name: "Commercio locale e PMI",
    shortDescription:
      "Romano di Lombardia ha 1.780 imprese attive: centro commerciale naturale della Bassa Bergamasca. Pattern: piccole attività locali che necessitano presenza digitale efficace senza budget agenzia, servizi B2B alle aziende del territorio, e-commerce di nicchia.",
    typicalClient: "Piccola attività locale 1-10 dipendenti, B2C o B2B locale",
    commonNeeds: [
      "sito vetrina efficace a costi sostenibili",
      "SEO locale Google Maps",
      "booking appuntamenti online",
      "automazione email + WhatsApp follow-up",
    ],
    recommendedServices: ["siti-wordpress", "automazioni-n8n-ai", "wordpress-performance"],
  },
];
